import { ReadMore } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    IconButton,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from 'src/allauth/auth';
import { useRoleLabels } from 'src/hooks/useRoleLabels';
import { FadedPaper, TrackedTextField } from 'src/theme/StyledFields';
import { timeAgo } from 'src/utils';
import AutoPaginatingList from '../components/AutoPaginatingList';
import CompletedContentIcon from '../components/CompletedContentIcon';
import OnboardingChecklist from '../components/onboarding/OnboardingChecklist';
import PreAssessmentCta from "../components/PreAssessmentCta";
import { useAppContext } from '../context/AppContext';
import { AthletePaymentAssignment, PaginatedResponse, PurposeNames } from '../object-actions/types/types';
import { assignmentHasPurposeEntitlement } from '../subscriptions/purposeEntitlements';
import { getCoachContentForPurpose, getContentStage } from "../utils/assignmentPermissions";
import { assignmentOnboardingPath, computeOnboardingSteps } from '../utils/onboarding';
import NoPaymentAssignments from './NoPaymentAssignments';

const ATHLETE_INVITE_TOAST =
    'Complete your profile, sign the waiver, then take your assessment. '
    + 'You can invite coaches or guardians from Manage assignment on your program.';

const Dashboard: React.FC = () => {
    const { applyRoleLabels } = useRoleLabels();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const athleteInviteToastShown = useRef(false);
    const [athletes, setAthletes] = useState<AthletePaymentAssignment[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(10);
    const [preAssessmentFilter, setPreAssessmentFilter] = useState<string>('all');
    const [sortBy, setSortBy] = useState<string>('newest');
    const user = useUser();
    const theme = useTheme();
    const { organization } = useAppContext();

    const orgWaiver = useMemo(
        () =>
            organization
                ? {
                    waiver_athlete: organization.waiver_athlete ?? organization.waiver_name,
                    waiver_adult: organization.waiver_adult,
                }
                : null,
        [organization],
    );

    // First not-done onboarding step for an assignment, or null when it's fully
    // set up. Drives both the single-assignment redirect and the per-row card.
    const assignmentSetupStep = useCallback(
        (assignment: AthletePaymentAssignment) =>
            computeOnboardingSteps({
                user,
                assignment,
                assignmentId: assignment.paymentassignment_id,
                orgWaiver,
                applyRoleLabels,
            }).currentStep,
        [user, orgWaiver, applyRoleLabels],
    );

    const searchSuffix = useMemo(() => {
        const qs = searchParams.toString();
        return qs ? `?${qs}` : '';
    }, [searchParams]);

    // Shortcut: a lone, incomplete assignment goes straight to its onboarding
    // screen rather than rendering a one-card dashboard. 0 and >1 render inline.
    const pendingOnboardingRedirect = useMemo(() => {
        if (loading || totalCount !== 1) return null;
        const only = athletes[0];
        if (!only?.paymentassignment_id) return null;
        return assignmentSetupStep(only)
            ? `${assignmentOnboardingPath(only.paymentassignment_id)}${searchSuffix}`
            : null;
    }, [loading, totalCount, athletes, assignmentSetupStep, searchSuffix]);

    useEffect(() => {
        if (pendingOnboardingRedirect) {
            navigate(pendingOnboardingRedirect, { replace: true });
        }
    }, [pendingOnboardingRedirect, navigate]);

    const basePath = useMemo(() => {
        const params = new URLSearchParams();
        if (limit > 0) params.set('limit', limit.toString());
        if (preAssessmentFilter === 'submitted') {
            params.set('pre_assessment_submitted', 'true');
        } else if (preAssessmentFilter === 'not_submitted') {
            params.set('pre_assessment_submitted', 'false');
        } else if (preAssessmentFilter === 'post_submitted') {
            params.set('post_assessment_submitted', 'true');
        }
        if (sortBy !== 'default') {
            params.set('sort_by', sortBy);
        }
        params.set('summary', 'true');
        const path = `/api/athlete-assignments${params.toString() ? `?${params.toString()}` : ''}`;
        console.log(`PAGINATION: building api path: ${path}`);
        return path;
    }, [limit, preAssessmentFilter, sortBy]);

    const handleSuccess = (data: PaginatedResponse<AthletePaymentAssignment>) => {
        if (data && data.results) {
            setAthletes(prev => [...prev, ...(data.results as AthletePaymentAssignment[])]);
            setTotalCount(data.count);
            setOffset(data.offset);
            setLimit(data.limit);
        }
        setLoading(false);
    };

    const handleError = (error: string) => {
        setError(error);
        setLoading(false);
    };

    useEffect(() => {
        if (athleteInviteToastShown.current) return;
        if (searchParams.get('athlete_invite_accepted') !== '1') return;
        athleteInviteToastShown.current = true;
        enqueueSnackbar(ATHLETE_INVITE_TOAST, { variant: 'info', autoHideDuration: 10000 });
        const next = new URLSearchParams(searchParams);
        next.delete('athlete_invite_accepted');
        const qs = next.toString();
        navigate({ pathname: '/dashboard', search: qs ? `?${qs}` : '' }, { replace: true });
    }, [searchParams, navigate, enqueueSnackbar]);

    useEffect(() => {
        console.log(`PAGINATION: resetting filters: limit: ${limit}, preAssessmentFilter: ${preAssessmentFilter}, sortBy: ${sortBy}`);
        setAthletes([]);
        setOffset(0);
        setError(null);
        setLoading(true);
    }, [limit, preAssessmentFilter, sortBy]);


    function renderSummary(assignment: AthletePaymentAssignment) {
        const { content_progress } = assignment;
        if (!content_progress) return null;

        const purposes = [
            { key: 'feedback_report', label: 'Feedback Report' }
        ];

        if (assignment.my_roles.includes('coach')) {
            purposes.push({ key: 'talking_points', label: 'Talking Points' });
        }
        if (assignmentHasPurposeEntitlement(assignment, 'curriculum')) {
            purposes.push({ key: 'curriculum', label: 'Curriculum' });
        }

        if (assignmentHasPurposeEntitlement(assignment, 'lesson_plan')) {
            purposes.push({ key: 'lesson_plan', label: 'Lesson Plan' });
        }

        // Growth Report (post-program). Only surfaces once a row has been
        // generated; the underlying visibility filter handles the
        // coach-vs-athlete/parent distinction (drafts visible to coaches only).
        purposes.push({ key: 'growth_report_week_4', label: 'Week 4 Growth Report' });
        purposes.push({ key: 'growth_report_week_8', label: 'Week 8 Growth Report' });
        purposes.push({ key: 'growth_report_week_12', label: 'Week 12 Growth Report' });

        const docs: React.ReactNode[] = [];

        if (assignment.waiver_required) {
            docs.push(
                <OnboardingChecklist
                    key="waiver-checklist"
                    variant="compact"
                    assignmentId={assignment.paymentassignment_id}
                />
            );
        } else {
            docs.push(<PreAssessmentCta key="pre_assessment_cta" assignment={assignment} />);

            purposes.forEach(({ key, label }) => {
                if (!assignmentHasPurposeEntitlement(assignment, key)) {
                    return;
                }
                const items = getCoachContentForPurpose(assignment, key as keyof typeof PurposeNames);
                const testIdSuffix = key.replace(/_/g, '-');
                if (items && Array.isArray(items) && items.length > 0) {
                    const status = getContentStage(assignment, items[0], applyRoleLabels).toLowerCase();
                    const segment = items[0]._type === 'LessonPlan' ? 'lesson-plan' : 'coach-content';
                    docs.push(
                        <Box key={key} display="flex" alignItems="center" gap={0.75}
                            data-testid={`program-stepper--${testIdSuffix}`}>
                            <CompletedContentIcon purpose={key} />
                            <Typography component="span">
                                <Link data-attr={key} to={`/${segment}/${items[0].id}`}>{label}</Link> <small>{status}</small></Typography>
                        </Box>
                    );
                }
            });
        }

        if (assignment.post_assessment_submitted_at) {
            docs.push(
                <Box key="post_assessment" display="flex" alignItems="center" gap={0.75}
                    data-testid="post-assessment-cta--submitted">
                    <CompletedContentIcon purpose="post_assessment" />
                    <Typography component="span">Post-Assessments Submitted</Typography>
                </Box>
            );
        }

        return (
            <CardHeader sx={{
                mb: 2,
                p: "0 0 0 10px",
                backgroundColor: theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.05)
                    : 'background.default',
                borderLeft: '3px solid',
                borderLeftColor: theme.palette.mode === "dark"
                    ? 'primary.main'
                    : 'divider',
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
            }}
                data-testid="dashboard-athlete-card"
                title={
                    <React.Fragment>
                        <Link to={`/program-progress/${assignment.paymentassignment_id}`} state={{ assignment }}>
                            {assignment.athlete?.id == user.id ? 'My Progress' : (assignment.athlete?.str ?? 'Assignment')}
                        </Link>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {docs.length > 0 ? 'Last update: ' : 'Assigned: '}
                            {timeAgo(assignment.last_update_at)}
                        </Typography>
                    </React.Fragment>
                }
                subheader={
                    <Box display="flex" flexWrap="wrap" gap={2} mt={1} alignItems="center">
                        {docs}
                    </Box>
                }
                action={
                    <IconButton
                        color="secondary"
                        component={Link}
                        sx={{ mr: 1 }}
                        to={`/program-progress/${assignment.paymentassignment_id}`}
                        state={{ assignment }}
                        data-attr={assignment.athlete?.id === user.id ? "my-progress-link" : "dashboard_program-progress-link"}
                        data-custom-id={String(assignment.paymentassignment_id)}
                    >
                        <ReadMore />
                    </IconButton>
                }
            />
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    // Compact "finish setup" card for an assignment that still has onboarding
    // steps left (includes empty, athlete-less payer rows). When the coach has
    // multiple assignments, lead with the athlete name so the list is scannable.
    function renderSetupCard(assignment: AthletePaymentAssignment) {
        const step = assignmentSetupStep(assignment);
        const showAthleteName = totalCount > 1;
        const athleteLabel =
            assignment.athlete?.id === user.id
                ? 'You'
                : (assignment.athlete?.str ?? 'Unassigned');
        return (
            <Card sx={{ mb: 2 }} data-testid="dashboard-setup-card">
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        {showAthleteName
                            ? `${athleteLabel} — Finish onboarding`
                            : 'Finish onboarding'}
                    </Typography>
                    {step && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {step.label}
                        </Typography>
                    )}
                    <Button
                        component={Link}
                        to={`${assignmentOnboardingPath(assignment.paymentassignment_id)}${searchSuffix}`}
                        variant="contained"
                        color="secondary"
                        data-attr="dashboard_finish-setup-cta"
                        data-custom-id={String(assignment.paymentassignment_id)}
                    >
                        Continue setup
                    </Button>
                </CardContent>
            </Card>
        );
    }

    function renderAthleteAssignment() {
        if (loading) {
            return <Box key={'loading-athlete-assignment'} sx={{ mb: 3, maxWidth: 1200, mx: 'auto' }}>
                Loading...
            </Box>
        }
        return <Box key={'athlete-assignments'} sx={{ mb: 3, maxWidth: 1200, mx: 'auto' }}>
            {athletes.length === 0 ? (
                <NoPaymentAssignments />
            ) : (
                athletes.map((athleteAssignment) => {
                    if (assignmentSetupStep(athleteAssignment)) {
                        return (
                            <React.Fragment key={athleteAssignment.paymentassignment_id}>
                                {renderSetupCard(athleteAssignment)}
                            </React.Fragment>
                        );
                    }
                    return (
                        <React.Fragment key={athleteAssignment.paymentassignment_id}>
                            {renderSummary(athleteAssignment)}
                        </React.Fragment>
                    );
                })
            )}
        </Box>
    }

    // A lone incomplete assignment redirects to onboarding (see effect above);
    // render nothing meaningful while that navigation is in flight.
    if (pendingOnboardingRedirect) {
        return <Box p={2} data-testid="dashboard-page">
            <Box sx={{ mb: 3, maxWidth: 1200, mx: 'auto' }}>Loading...</Box>
        </Box>;
    }

    if (athletes.length === 1 && offset === 0 && preAssessmentFilter === 'all') { // no toolbar
        return <Box p={2} data-testid="dashboard-page">
            <OnboardingChecklist
                variant="compact"
                assignmentId={athletes[0]?.paymentassignment_id ?? null}
            />
            {renderAthleteAssignment()}
        </Box>;
    }

    return (
        <Box p={2} data-testid="dashboard-page">
            {athletes.length === 1 && offset === 0 && preAssessmentFilter === 'all' && (
                <OnboardingChecklist
                    variant="compact"
                    assignmentId={athletes[0]?.paymentassignment_id ?? null}
                />
            )}
            {/* Toolbar with filters */}
            <FadedPaper sx={{ mb: 2 }}>
                <Toolbar sx={{ flexWrap: 'wrap', gap: 1, p: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {applyRoleLabels('Athlete Assignments')} ({totalCount})
                    </Typography>
                    <TrackedTextField
                        select
                        label="Filter By"
                        value={preAssessmentFilter}
                        onChange={(e) => setPreAssessmentFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200, mr: 2 }}
                        data-attr="dashboard_filter-pre-assessment"
                    >
                        <MenuItem value="all">All Assignments</MenuItem>
                        <MenuItem value="submitted">Pre-Assessment Submitted</MenuItem>
                        <MenuItem value="not_submitted">Pre-Assessment Not Submitted</MenuItem>
                        <MenuItem value="post_submitted">Post-Assessment Submitted</MenuItem>
                    </TrackedTextField>
                    <Tooltip
                        title={
                            sortBy === 'newest'
                                ? 'Newest assignments first (by assignment date)'
                                : sortBy === 'oldest'
                                    ? 'Oldest assignments first (by assignment date)'
                                    : 'Pre-assessment submitted first, then by earliest submission date; unsubmitted last'
                        }
                        placement="top"
                    >
                        <TrackedTextField
                            select
                            label="Sort By"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            size="small"
                            sx={{ minWidth: 200 }}
                            data-attr="dashboard_sort-by"
                        >
                            <MenuItem value="newest">Newest</MenuItem>
                            <MenuItem value="oldest">Oldest</MenuItem>
                        </TrackedTextField>
                    </Tooltip>
                </Toolbar>
            </FadedPaper>

            {/* Auto-paginating athlete list */}
            <AutoPaginatingList
                basePath={basePath}
                count={totalCount}
                limit={limit}
                offset={offset}
                onSuccess={handleSuccess}
                onError={handleError}
            >
                {renderAthleteAssignment()}
            </AutoPaginatingList>
        </Box>
    );
};

export default Dashboard;
